import * as React from "react"
import { injectIntl, InjectedIntlProps } from "react-intl"
import DebtAnalysisComponent, { DataShape } from "./debt-analysis-component"
import { Loan, LoanWithDecomposition, Bank } from "../../../model"
import { ColorsHelper } from "../../../util/colors"
import { getAverageRate, getAverageRatePerFixed, getAverageRatePerVariable } from "../../../domain/sfp/loan-domain"
import { ChartData } from "chart.js";

const rateLabels = {
	averageRate              : "Taux moyen",
	averageRatePerFixed      : "Taux moyen d'intérêt à taux fixe",
	averageRatePerVariable   : "Taux moyen d'intérêt à taux variable"
}
const bankNC = "Banque NC"

class DebtAnalysis
extends React.Component<DebtAnalysisProps & InjectedIntlProps, State> {
	constructor(props) {
		super(props)
		this.state = { year: (new Date()).getFullYear() }
	}

	render() {
		return (
			<DebtAnalysisComponent
			data={this.getData(this.props.loansWithDecomposition, this.state.year)}
			dataRate={this.getDataRate(this.props.loansWithDecomposition)}
			loading={this.props.loading}
			onYearChange={e => this.handleYearChange(e)}
			year={this.state.year}
			years={this.props.years} />
		)
	}

	private getData(loansWithDecomposition: LoanWithDecomposition[], year: number) {
		const initialOffset           = 30
		const perRateLabels           = ["Taux fixe", "Taux variable"]
		const perRateBackgroundColor  = ColorsHelper.getEquidistantPastelColors(2, initialOffset)
		const data: DataShape = {
			interestPerBank   : { labels: [],            datasets: [{ backgroundColor: [],                     data: [] }] },
			interestPerRate   : { labels: perRateLabels, datasets: [{ backgroundColor: perRateBackgroundColor, data: [0, 0] }] },
			nominalPerBank    : { labels: [],            datasets: [{ backgroundColor: [],                     data: [] }] },
			nominalPerRate    : { labels: perRateLabels, datasets: [{ backgroundColor: perRateBackgroundColor, data: [0, 0] }] },
			reimboursedPerBank: { labels: [],            datasets: [{ backgroundColor: [],                     data: [] }] },
			reimboursedPerRate: { labels: perRateLabels, datasets: [{ backgroundColor: perRateBackgroundColor, data: [0, 0] }] },
			remainingPerBank  : { labels: [],            datasets: [{ backgroundColor: [],                     data: [] }] },
			remainingPerRate  : { labels: perRateLabels, datasets: [{ backgroundColor: perRateBackgroundColor, data: [0, 0] }] },
		}

		const banks: Bank[] = []

		loansWithDecomposition.forEach(loanWithDecomposition => {
			const loan = loanWithDecomposition.loan
			let bankIndex = banks.findIndex(bank => {
				if (null === bank && null === loan.bank) return true
				if (bank && loan.bank && bank.id === loan.bank.id) return true
				return false
			})
			let interest = 0
			let reimboursed = 0
			let remaining = 0

			const decomposition = loanWithDecomposition.decomposition
			decomposition.paymentDates.forEach((paymentDate, i) => {
				if (paymentDate.getFullYear() === year) {
					interest    += decomposition.interests[i]
					reimboursed += decomposition.reimboursedCapitals[i]
					remaining    = decomposition.remainingCapitals[i]
				}
			})

			const interestPerBankData = data.interestPerBank.datasets[0].data as number[]
			const nominalPerBankData = data.nominalPerBank.datasets[0].data as number[]
			const reimboursedPerBankData = data.reimboursedPerBank.datasets[0].data as number[]
			const reimainingPerBankData = data.remainingPerBank.datasets[0].data as number[]

			if (-1 === bankIndex) {
				banks.push(loan.bank)

				interestPerBankData.push(interest)
				nominalPerBankData.push(loan.amount)
				reimboursedPerBankData.push(reimboursed)
				reimainingPerBankData.push(remaining)

				bankIndex = banks.length - 1
			} else {
				data.nominalPerBank.datasets[0].data[bankIndex] = interestPerBankData[bankIndex] + loan.amount
				data.interestPerBank.datasets[0].data[bankIndex] = nominalPerBankData[bankIndex] + interest
				data.reimboursedPerBank.datasets[0].data[bankIndex] = reimboursedPerBankData[bankIndex] + reimboursed
				data.remainingPerBank.datasets[0].data[bankIndex] = reimainingPerBankData[bankIndex] + remaining
			}

			const rateIndex = loan.variableRate ? 1 : 0
			const interestPerRateData = data.interestPerRate.datasets[0].data as number[]
			const nominalPerRateData = data.nominalPerRate.datasets[0].data as number[]
			const reimboursedPerRateData = data.reimboursedPerRate.datasets[0].data as number[]
			const reimainingPerRateData = data.remainingPerRate.datasets[0].data as number[]

			interestPerRateData[rateIndex] += interest
			nominalPerRateData[rateIndex] += loan.amount
			reimboursedPerRateData[rateIndex] += reimboursed
			reimainingPerRateData[rateIndex] += remaining
		})

		// Ajout des banques dans les libellés des graphiques
		data.interestPerBank.labels
		= data.nominalPerBank.labels
		= data.reimboursedPerBank.labels
		= data.remainingPerBank.labels
		= banks.map(b => b ? b.label : bankNC)

		const colors = ColorsHelper.getEquidistantPastelColors(banks.length, initialOffset)
		data.interestPerBank.datasets[0].backgroundColor    = colors
		data.nominalPerBank.datasets[0].backgroundColor     = colors
		data.reimboursedPerBank.datasets[0].backgroundColor = colors
		data.remainingPerBank.datasets[0].backgroundColor   = colors

		return data
	}

	private getDataRate(loansWithDecomposition: LoanWithDecomposition[]) {
		const year = this.state.year
		const data = [
			{ label: rateLabels.averageRate, data: getAverageRate(loansWithDecomposition, year)},
			{ label: rateLabels.averageRatePerFixed, data: getAverageRatePerFixed(loansWithDecomposition, year)},
			{ label: rateLabels.averageRatePerVariable, data: getAverageRatePerVariable(loansWithDecomposition, year)}
		]
		return data
	}

	private handleYearChange(event: React.SyntheticEvent<Element>): void {
		const select = event.target as HTMLSelectElement
		this.setState({ year: Number.parseInt(select.value) })
	}
}

export default injectIntl(DebtAnalysis)

export interface DebtAnalysisProps {
	loading: boolean,
	loansWithDecomposition: LoanWithDecomposition[],
	years: number[]
}

interface State {
	year: number
}


// WEBPACK FOOTER //
// ./src/component/debt/debt-analysis/index.tsx